import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { IconInformation, Link, SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';
import { VerticalRhythm } from 'mage-components';
import {
  AddMoreIfAllowed,
  analytics,
  defaultContainerPropTypes,
  HorizontalWrapper,
  moetPropTypes,
  Alert,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CAPS } from '@/constants';
import { CY_STAKEHOLDER_CONTAINER_IDS } from '@/constants/stakeholders';
import {
  STAKEHOLDER_ROLES_V2,
  stakeholderUtils,
  withStakeholderLogic,
} from '@/containers/withStakeholderLogic';
import {
  BackButton,
  ContainerTemplate,
  CallToActionButton,
  StakeholderFormCard,
  ToggleWrappedContent,
} from '@/components';
import { getMerchantAwareTranslator, getMerchantAwareArrayTranslator } from '@/selectors/merchant';

import { getDefaultState, getLookupState } from './selectors';
import { ANALYTICS_EVENTS } from './analyticsEvents';
import { prepareStakeholderEventData } from '@/utils/analytics';

const tPrefix = 'products-fe.containers.ultimate_beneficial_owner';

export class UltimateBeneficialOwner extends React.Component {
  constructor(props) {
    super(props);

    const { toggleValue } = props.defaultState;
    this.state = { ctaClicked: false, toggleValue };

    this.onToggle = this.onToggle.bind(this);
    this.onNext = this.onNext.bind(this);
    this.getBody = this.getBody.bind(this);
    this.onRemove = this.onRemove.bind(this);
  }

  onToggle(onAddStakeholderFn, onRemoveAllStakeholdersFn) {
    return toggleValue =>
      this.setState({ toggleValue }, toggleValue ? onAddStakeholderFn : onRemoveAllStakeholdersFn);
  }

  getBody() {
    const { stakeholders, mid } = this.props;

    return {
      payload: {
        headers: {
          'X-PRODUCTS-CONTAINER': 'UltimateBeneficialOwner',
        },
        params: {
          mid,
        },
        body: stakeholderUtils.getStakeholdersMappedValues(stakeholders),
      },
    };
  }

  onNext() {
    const { onNextWrapper, stakeholders, roleConstraints } = this.props;

    const { toggleValue } = this.state;

    this.setState({ ctaClicked: true });

    onNextWrapper(
      this.getBody(),
      toggleValue !== undefined &&
        stakeholderUtils.isStakeholderConstraintsValid(stakeholders, roleConstraints) &&
        stakeholderUtils.isStakeholdersValid(stakeholders),
    );
  }

  onRemove(index) {
    const { min, onRemoveStakeholder, stakeholders } = this.props;

    const currentStakeholdersCount = stakeholders.filter(
      stakeholder => !stakeholder.roles.includes(STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT),
    ).length;

    return currentStakeholdersCount > Math.max(1, min)
      ? () => onRemoveStakeholder(index)
      : undefined;
  }

  render() {
    const {
      availableRoles = [],
      error,
      hasBack,
      isDisabled,
      isLoading,
      max,
      min,
      onAddStakeholder,
      onBack,
      onBlurStakeholder,
      onChangeRoles,
      onChangeStakeholder,
      onRemoveAllStakeholders,
      onTouchPersonalId,
      roleConstraints,
      stakeholders,
      t,
      tMerchantAware,
      tArrayMerchantAware,
    } = this.props;
    const { ctaClicked, toggleValue } = this.state;
    const { lookupState: { isLookup = false } = {} } = this.props;

    const currentStakeholdersCount = stakeholders.filter(
      stakeholder => !stakeholder.roles.includes(STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT),
    ).length;

    // currentStakeholderCount is the actual visible count of stakeholder form cards
    // We then multiply by the following expression to get the total number of fields:
    //    8: the number of fields in the stakeholder form (name, birth date, address, etc)
    //    availableRoles.length: represents the number of toggles below the form
    //    1 or 0: depending on if there is a remove button or not for the stakeholder card
    const tabIndex =
      currentStakeholdersCount *
        (8 + availableRoles.length + (currentStakeholdersCount > Math.max(1, min) ? 1 : 0)) +
      1;

    let idx = -1;

    const stakeholderFragment = (stakeholder, index) => {
      // We need to render a "placeholder" to keep the correct index going for updating the
      // corresponding stakeholder, otherwise the index would refer to another one
      if (stakeholder.roles.includes(STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT)) {
        return null;
      }

      ++idx;

      return (
        <React.Fragment key={`ubos-${stakeholder.reactFormId}`}>
          {toggleValue === true && isLookup && (
            <>
              <Alert content={t(`${tPrefix}.messages.lookup`)} icon={IconInformation} />
              <SpacerVertical spaceToken="space/300" />
            </>
          )}
          <StakeholderFormCard
            autoFocus={index === currentStakeholdersCount - 1}
            tabIndexStart={
              1 +
              idx *
                (9 + availableRoles.length + (currentStakeholdersCount > Math.max(1, min) ? 1 : 0))
            }
            stakeholder={stakeholder}
            availableRoles={availableRoles}
            disabledRoles={stakeholderUtils.getDisabledRoles(
              roleConstraints,
              stakeholders,
              stakeholder.roles,
            )}
            onBlur={key => onBlurStakeholder(index, key)}
            onChange={(key, value) => onChangeStakeholder(index, key, value)}
            onChangeRoles={(role, val) => onChangeRoles(index, role, val)}
            onTouchPersonalId={() => onTouchPersonalId(index)}
            onRemove={this.onRemove(index)}
            isDisabled={isDisabled}
            name={`ubo-${index}`}
          />
          <SpacerVertical spaceToken="space/150" />
        </React.Fragment>
      );
    };

    return (
      <ContainerTemplate
        title={tMerchantAware(`${tPrefix}.title`)}
        subtitle={tArrayMerchantAware(`${tPrefix}.subtitle`).map(({ text, link }, index) =>
          link ? (
            <Link key={index} href={link} target="_blank">
              {text}
            </Link>
          ) : (
            <span key={index}>{text}</span>
          ),
        )}
        error={error}
      >
        <ToggleWrappedContent
          error={ctaClicked && toggleValue === undefined}
          toggleOnChange={this.onToggle(onAddStakeholder, onRemoveAllStakeholders)}
          toggleValue={toggleValue}
          yesContent={
            <>
              <Alert content={tMerchantAware(`${tPrefix}.infobox`)} icon={IconInformation} />
              <VerticalRhythm spacing={{ mobile: 6 }} />
              {stakeholders.map(stakeholderFragment)}
              <AddMoreIfAllowed
                tabIndex={tabIndex}
                onClick={onAddStakeholder}
                text={t(`${tPrefix}.buttons.add_person`)}
                currentCount={stakeholders.length}
                maxCount={max}
                disabled={isDisabled}
              />
              <SpacerVertical spaceToken="space/150" />
            </>
          }
          commonContent={
            <>
              <HorizontalWrapper
                display="inline-flex"
                alignItems="center"
                justifyContent="flex-end"
              >
                {hasBack && (
                  <>
                    <BackButton
                      tabIndex={tabIndex + (stakeholders.length < max ? 2 : 1)}
                      onClick={onBack}
                      disabled={isDisabled}
                    >
                      {t(`${tPrefix}.buttons.back`)}
                    </BackButton>
                    <SpacerHorizontal spaceToken="space/300" />
                  </>
                )}
                <CallToActionButton
                  tabIndex={tabIndex + (stakeholders.length < max ? 1 : 0)}
                  onClick={this.onNext}
                  disabled={isDisabled}
                  loading={isLoading}
                  {...generateCyElemId(CY_STAKEHOLDER_CONTAINER_IDS.BTN_SUBMIT, window)}
                >
                  {t(`${tPrefix}.buttons.next`)}
                </CallToActionButton>
              </HorizontalWrapper>
            </>
          }
        />
      </ContainerTemplate>
    );
  }
}

UltimateBeneficialOwner.propTypes = {
  ...defaultContainerPropTypes,
  ...moetPropTypes.defaultStakeholderContainerPropTypes,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tArrayMerchantAware: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  defaultState: PropTypes.shape({
    toggleValue: PropTypes.bool,
  }),
};

export default compose(
  connect(state => ({
    defaultState: getDefaultState(state),
    lookupState: getLookupState(state),
    tMerchantAware: getMerchantAwareTranslator(state),
    tArrayMerchantAware: getMerchantAwareArrayTranslator(state),
  })),
  withStakeholderLogic(CAPS.V2_MERCHANTS_STAKEHOLDERS_PUT, {
    capabilityCallback: ({ payload }) => {
      if (payload.body?.length) {
        const data = prepareStakeholderEventData(
          payload.body,
          STAKEHOLDER_ROLES_V2.ULTIMATE_BENEFICIAL_OWNER,
        );
        analytics.logEvent(ANALYTICS_EVENTS.submitUbos, data);
      }
    },
  }),
)(UltimateBeneficialOwner);
